<template>
  <Layout>
    <div class="campaign-edit">
      <div class="page-header">
        <div>
          <h2>{{ isEdit ? '编辑广告计划' : '创建广告计划' }}</h2>
          <p>{{ isEdit ? '修改现有广告计划' : '创建新的广告计划' }}</p>
        </div>
      </div>

      <el-form
        ref="campaignForm"
        :model="campaignForm"
        label-width="120px"
        class="campaign-form"
        v-loading="loading">

        <el-card class="form-card">
          <div slot="header">
            <span>基本信息</span>
          </div>

          <el-form-item label="计划类型" required>
            <el-radio-group v-model="campaignForm.campaign_type" @change="handleCampaignTypeChange">
              <el-radio label="standard">标准广告</el-radio>
              <el-radio label="article">软文广告</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="计划名称" required>
            <el-input v-model.trim="campaignForm.name" placeholder="输入广告计划名称" />
          </el-form-item>

          <el-form-item label="广告活动" required>
            <el-select
              v-model="campaignForm.activity_id"
              filterable
              placeholder="选择所属广告活动"
              style="width: 100%"
              @change="handleActivityChange">
              <el-option
                v-for="activity in campaignActivities"
                :key="activity.id"
                :label="activity.name"
                :value="activity.id" />
            </el-select>
          </el-form-item>

          <el-form-item v-if="isArticleCampaign" :label="articlePriceLabel" required>
            <el-input-number
              v-model="campaignForm.total_budget"
              :min="10000"
              :step="100"
              controls-position="right"
              style="width: 100%" />
          </el-form-item>

          <el-form-item label="状态" required>
            <div v-if="!isEdit" class="review-status-note">
              新建后进入审核，请耐心等待。
            </div>
            <el-radio-group v-else v-model="campaignForm.status">
              <el-radio label="reviewing">审核中</el-radio>
              <el-radio label="active">运行中</el-radio>
              <el-radio label="paused">已暂停</el-radio>
              <el-radio label="stopped">已停止</el-radio>
              <el-radio label="completed">已完成</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-card>

        <el-card class="form-card">
          <div slot="header">
            <span>广告创意</span>
          </div>

          <template v-if="isArticleCampaign">
            <el-form-item label="软文素材" required>
              <div class="material-selector">
                <el-select
                  v-model="selectedSoftMaterialId"
                  filterable
                  placeholder="选择DOCX软文素材"
                  popper-class="docx-material-dropdown"
                  @change="handleSoftMaterialSelect">
                  <el-option
                    v-for="item in docxMaterials"
                    :key="item.id"
                    :label="item.filename"
                    :value="item.id">
                    <div class="material-option doc-option">
                      <el-image
                        v-if="item.cover_url"
                        style="width: 50px; height: 30px"
                        :src="getMaterialPreviewUrl(item, 'cover')"
                        fit="cover" />
                      <div v-else class="doc-preview-fallback small">DOCX</div>
                      <div class="doc-option-copy">
                        <span>{{ item.filename }}</span>
                        <small>{{ item.word_count || 0 }} 字</small>
                      </div>
                    </div>
                  </el-option>
                </el-select>

                <div class="material-preview material-preview-doc" v-if="selectedSoftMaterial">
                  <el-image
                    v-if="selectedSoftMaterial.cover_url"
                    style="width: 220px; height: 132px"
                    :src="getMaterialPreviewUrl(selectedSoftMaterial, 'cover')"
                    fit="cover" />
                  <div class="doc-preview-fallback large" v-else>DOCX</div>
                  <div class="doc-preview-copy">
                    <strong>{{ selectedSoftMaterial.title || selectedSoftMaterial.filename }}</strong>
                    <span>{{ selectedSoftMaterial.summary || '图文素材' }}</span>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="留资代码块" required>
              <el-input
                v-model.trim="campaignForm.creative.lead_form_embed_code"
                type="textarea"
                :rows="6"
                placeholder="输入软文广告使用的留资代码块" />
              <div class="form-item-tips">
                <i class="el-icon-info" />
                <span>可填写 iframe 或嵌入式 HTML 代码块，用于软文页内留资。</span>
              </div>
            </el-form-item>
          </template>

          <template v-else>
            <el-form-item label="标题" required>
              <el-input v-model.trim="campaignForm.creative.title" placeholder="输入广告标题" />
            </el-form-item>

            <el-form-item label="描述" required>
              <el-input
                v-model.trim="campaignForm.creative.description"
                type="textarea"
                :rows="3"
                placeholder="输入广告描述" />
            </el-form-item>

            <el-form-item label="落地页URL" required>
              <el-input v-model.trim="campaignForm.creative.landing_page_url" placeholder="输入广告落地页URL" />
            </el-form-item>

            <el-form-item label="调起链接">
              <el-input v-model.trim="campaignForm.creative.deep_link_url" placeholder="输入APP调起链接（可选）" />
            </el-form-item>

            <el-form-item label="竞胜通知">
              <el-input v-model.trim="campaignForm.creative.win_notice_url" placeholder="输入竞胜通知URL（可选）" />
            </el-form-item>

            <el-form-item label="展示监测">
              <el-input v-model.trim="campaignForm.creative.impression_tracking_url" placeholder="输入展示监测URL（可选）" />
            </el-form-item>

            <el-form-item label="点击监测">
              <el-input v-model.trim="campaignForm.creative.click_tracking_url" placeholder="输入点击监测URL（可选）" />
            </el-form-item>

            <el-form-item label="调端通知">
              <el-input v-model.trim="campaignForm.creative.deeplink_tracking_url" placeholder="输入调端通知URL（可选）" />
            </el-form-item>

            <el-divider content-position="left">物料选择</el-divider>

            <el-form-item label="广告样式">
              <el-select
                v-model="selectedAdStyle"
                filterable
                placeholder="选择广告样式"
                @change="handleAdStyleChange">
                <el-option
                  v-for="(style, id) in adStyles"
                  :key="id"
                  :label="`${style.description} (${style.material_size}, ${style.w}x${style.h})`"
                  :value="id" />
              </el-select>
              <div class="form-item-tips" v-if="selectedAdStyleInfo">
                <i class="el-icon-info" />
                <span>物料尺寸: {{ selectedAdStyleInfo.w }}x{{ selectedAdStyleInfo.h }}，类型: {{ selectedAdStyleInfo.material_size }}</span>
              </div>
            </el-form-item>

            <el-form-item label="三图" v-if="showThreeImageMaterial">
              <div class="three-image-container">
                <div v-for="(_, index) in 3" :key="index" class="three-image-item">
                  <div class="three-image-selector">
                    <el-select
                      v-model="selectedImageIds[index]"
                      filterable
                      :placeholder="`选择第${index + 1}张图片`"
                      @change="(val) => handleThreeImageSelect(val, index)">
                      <el-option
                        v-for="item in imageMaterials"
                        :key="`${item.id}-${index}`"
                        :label="item.filename"
                        :value="item.id">
                        <div class="material-option">
                          <el-image style="width: 50px; height: 30px" :src="getMaterialPreviewUrl(item)" fit="cover" />
                          <span>{{ item.filename }}</span>
                        </div>
                      </el-option>
                    </el-select>

                    <div class="material-preview" v-if="selectedImages[index]">
                      <el-image style="width: 120px; height: 80px" :src="getMaterialPreviewUrl(selectedImages[index])" fit="contain" />
                    </div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="图片" v-if="showImageMaterial">
              <div class="material-selector">
                <el-select
                  v-model="selectedImageId"
                  filterable
                  placeholder="选择图片物料"
                  @change="handleImageSelect">
                  <el-option
                    v-for="item in imageMaterials"
                    :key="item.id"
                    :label="item.filename"
                    :value="item.id">
                    <div class="material-option">
                      <el-image style="width: 50px; height: 30px" :src="getMaterialPreviewUrl(item)" fit="cover" />
                      <span>{{ item.filename }}</span>
                    </div>
                  </el-option>
                </el-select>

                <div class="material-preview" v-if="selectedImage">
                  <el-image style="width: 200px; height: 120px" :src="getMaterialPreviewUrl(selectedImage)" fit="contain" />
                </div>
              </div>
            </el-form-item>

            <el-form-item label="视频" v-if="showVideoMaterial">
              <div class="material-selector">
                <el-select
                  v-model="selectedVideoId"
                  filterable
                  placeholder="选择视频物料"
                  @change="handleVideoSelect">
                  <el-option
                    v-for="item in videoMaterials"
                    :key="item.id"
                    :label="item.filename"
                    :value="item.id" />
                </el-select>

                <div class="material-preview" v-if="selectedVideo">
                  <el-button size="small" type="primary" @click="previewVideo">预览视频</el-button>
                  <span class="video-filename">{{ selectedVideo.filename }}</span>
                </div>
              </div>
            </el-form-item>
          </template>
        </el-card>

        <el-card class="form-card">
          <div slot="header">
            <span>定向设置</span>
          </div>

          <template v-if="isArticleCampaign">
            <el-form-item label="媒体代码位" required>
              <el-select
                v-model="campaignForm.targeting.media_slot_ids"
                multiple
                filterable
                placeholder="请选择软广媒体代码位"
                style="width: 100%"
                popper-class="media-slot-dropdown">
                <el-option
                  v-for="slot in articleMediaSlots"
                  :key="slot.id"
                  :label="slot.name"
                  :value="slot.id">
                  <div class="media-slot-option">
                    <div class="media-slot-name">{{ slot.name }}</div>
                    <div class="media-slot-info">
                      <el-tag size="mini" :type="getPlatformTagType(slot.platform)">{{ getPlatformName(slot.platform) }}</el-tag>
                      <el-tag size="mini" :type="getAdTypeTagType(slot.adType)">{{ getAdTypeName(slot.adType) }}</el-tag>
                    </div>
                  </div>
                </el-option>
              </el-select>
              <div class="form-item-tips">
                <i class="el-icon-info" />
                <span>软文广告只能投放到软广媒体代码位。</span>
              </div>
            </el-form-item>
          </template>

          <template v-else>
            <el-form-item label="点击过滤">
              <el-switch
                v-model="campaignForm.targeting.click_filter"
                :active-value="true"
                :inactive-value="false" />
            </el-form-item>

            <el-form-item label="性别">
              <el-checkbox-group v-model="campaignForm.targeting.gender">
                <el-checkbox label="male">男</el-checkbox>
                <el-checkbox label="female">女</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <el-form-item label="年龄段">
              <el-checkbox-group v-model="campaignForm.targeting.age_range">
                <el-checkbox :label="18">18-24岁</el-checkbox>
                <el-checkbox :label="25">25-34岁</el-checkbox>
                <el-checkbox :label="35">35-44岁</el-checkbox>
                <el-checkbox :label="45">45岁以上</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <el-form-item label="设备类型">
              <el-checkbox-group v-model="campaignForm.targeting.device_types">
                <el-checkbox label="ios">iOS</el-checkbox>
                <el-checkbox label="android">Android</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <el-form-item label="网络类型">
              <el-checkbox-group v-model="campaignForm.targeting.network_types">
                <el-checkbox label="wifi">WiFi</el-checkbox>
                <el-checkbox label="4g">4G</el-checkbox>
                <el-checkbox label="5g">5G</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <el-form-item label="手机品牌">
              <div class="brand-checkbox-container">
                <div v-for="brand in phoneBrands" :key="brand.value" class="brand-checkbox-item">
                  <input
                    :id="'brand-' + brand.value"
                    type="checkbox"
                    :value="brand.value"
                    :checked="isBrandSelected(brand.value)"
                    @change="toggleBrand(brand.value)" />
                  <label :for="'brand-' + brand.value">{{ brand.label }}</label>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="兴趣标签">
              <el-select
                v-model="campaignForm.targeting.interests"
                multiple
                filterable
                placeholder="请选择兴趣标签"
                style="width: 100%">
                <el-option
                  v-for="item in interestTags"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="应用安装">
              <el-select
                v-model="campaignForm.targeting.installed_apps"
                multiple
                filterable
                placeholder="请选择应用安装列表"
                style="width: 100%">
                <el-option
                  v-for="(item, index) in appList"
                  :key="index + '-' + item.package_name"
                  :label="item.app_name"
                  :value="item.package_name" />
              </el-select>
            </el-form-item>

            <el-form-item label="人群定向">
              <el-select
                v-model="campaignForm.targeting.audience_package_ids"
                multiple
                filterable
                placeholder="请选择人群包"
                style="width: 100%"
                popper-class="audience-package-dropdown">
                <el-option
                  v-for="pkg in audiencePackages"
                  :key="pkg.id"
                  :label="pkg.name || pkg.filename || '未命名人群包'"
                  :value="pkg.id">
                  <div class="audience-package-option">
                    <div class="audience-package-name">{{ pkg.name || pkg.filename || '未命名人群包' }}</div>
                    <div class="audience-package-info">
                      <span>ID: {{ pkg.id }}</span>
                    </div>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="媒体代码位">
              <el-select
                v-model="campaignForm.targeting.media_slot_ids"
                multiple
                filterable
                placeholder="请选择媒体代码位"
                style="width: 100%"
                popper-class="media-slot-dropdown">
                <el-option
                  v-for="slot in standardMediaSlots"
                  :key="slot.id"
                  :label="slot.name"
                  :value="slot.id">
                  <div class="media-slot-option">
                    <div class="media-slot-name">{{ slot.name }}</div>
                    <div class="media-slot-info">
                      <el-tag size="mini" :type="getPlatformTagType(slot.platform)">{{ getPlatformName(slot.platform) }}</el-tag>
                      <el-tag size="mini" :type="getAdTypeTagType(slot.adType)">{{ getAdTypeName(slot.adType) }}</el-tag>
                    </div>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="包名定向">
              <div class="package-names-container">
                <el-tag
                  v-for="(packageName, index) in campaignForm.targeting.package_names"
                  :key="index"
                  closable
                  @close="removePackageName(index)"
                  class="package-name-tag">
                  {{ packageName }}
                </el-tag>
                <el-input
                  v-if="inputVisible"
                  ref="saveTagInput"
                  v-model="inputValue"
                  size="small"
                  class="input-new-tag"
                  @keyup.enter.native="handleInputConfirm"
                  @blur="handleInputConfirm"
                  placeholder="输入包名，如 com.example.app" />
                <el-button v-else class="button-new-tag" size="small" @click="showInput">+ 添加包名</el-button>
              </div>
            </el-form-item>
          </template>
        </el-card>

        <div class="form-actions">
          <el-button @click="$router.push('/campaigns')">取消</el-button>
          <el-button type="primary" @click="submitForm">保存</el-button>
        </div>
      </el-form>

      <el-dialog title="视频预览" :visible.sync="videoDialogVisible" width="70%">
        <div class="video-container" v-if="selectedVideo">
          <video controls style="width: 100%; max-height: 70vh">
            <source :src="getMaterialPreviewUrl(selectedVideo)" type="video/mp4">
            您的浏览器不支持视频播放
          </video>
        </div>
      </el-dialog>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { mapGetters, mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'

const DEFAULT_STANDARD_BID_PRICE = 100
const DEFAULT_STANDARD_DAILY_BUDGET = 1000
const DEFAULT_STANDARD_TOTAL_BUDGET = 50000

function createEmptyCampaign() {
  return {
    id: '',
    campaign_type: 'standard',
    name: '',
    activity_id: '',
    activity_name: '',
    bid_price: DEFAULT_STANDARD_BID_PRICE,
    daily_budget: DEFAULT_STANDARD_DAILY_BUDGET,
    total_budget: DEFAULT_STANDARD_TOTAL_BUDGET,
    status: 'reviewing',
    creative: {
      id: '',
      title: '',
      description: '',
      landing_page_url: '',
      lead_form_embed_url: '',
      lead_form_embed_code: '',
      deep_link_url: '',
      win_notice_url: '',
      impression_tracking_url: '',
      click_tracking_url: '',
      deeplink_tracking_url: '',
      materials: []
    },
    targeting: {
      id: '',
      gender: [],
      age_range: [],
      device_types: [],
      network_types: [],
      interests: [],
      installed_apps: [],
      brand: [],
      audience_package_ids: [],
      media_slot_ids: [],
      click_filter: false,
      package_names: []
    }
  }
}

export default {
  name: 'CampaignEdit',
  components: {
    Layout
  },
  data() {
    return {
      phoneBrands: [
        { value: 'xiaomi', label: '小米' },
        { value: 'huawei', label: '华为' },
        { value: 'honor', label: '荣耀' },
        { value: 'apple', label: '苹果' },
        { value: 'oppo', label: 'OPPO' },
        { value: 'vivo', label: 'vivo' },
        { value: 'samsung', label: '三星' },
        { value: 'meizu', label: '魅族' }
      ],
      platformOptions: [
        { value: 'tencent', label: '腾讯广告' },
        { value: 'bytedance', label: '字节跳动' },
        { value: 'baidu', label: '百度联盟' },
        { value: 'kuaishou', label: '快手' },
        { value: 'alibaba', label: '阿里妈妈' },
        { value: 'xiaomi', label: '小米广告' },
        { value: 'oppo', label: 'OPPO广告' },
        { value: 'vivo', label: 'vivo广告' },
        { value: 'huawei', label: '华为广告' },
        { value: 'sohu', label: '搜狐' },
        { value: 'meishu', label: '美数' },
        { value: 'shunfei', label: '舜飞' },
        { value: 'zhangyue', label: '掌阅' },
        { value: 'wifi_key', label: 'Wifi万能钥匙' },
        { value: 'xiaohongshu_article', label: '小红书软广' },
        { value: 'wechat_article', label: '公众号软广' },
        { value: 'self_media_article', label: '自媒体软广' },
        { value: 'portal_article', label: '门户媒体软广' },
        { value: 'other', label: '其他平台' }
      ],
      adTypeOptions: [
        { value: 'banner', label: '横幅广告' },
        { value: 'native', label: '信息流广告' },
        { value: 'interstitial', label: '插屏广告' },
        { value: 'rewarded_video', label: '激励视频' },
        { value: 'splash', label: '开屏广告' },
        { value: 'draw', label: 'Draw信息流' },
        { value: 'soft_article', label: '软文广告' }
      ],
      campaignForm: createEmptyCampaign(),
      selectedImageId: '',
      selectedVideoId: '',
      selectedSoftMaterialId: '',
      selectedImageIds: ['', '', ''],
      videoDialogVisible: false,
      adStyles: {},
      selectedAdStyle: '',
      loadingAdStyles: false,
      previousStandardBudget: {
        bid_price: DEFAULT_STANDARD_BID_PRICE,
        daily_budget: DEFAULT_STANDARD_DAILY_BUDGET,
        total_budget: DEFAULT_STANDARD_TOTAL_BUDGET
      },
      inputVisible: false,
      inputValue: ''
    }
  },
  computed: {
    ...mapState([
      'loading',
      'campaignActivities',
      'materials',
      'interestTags',
      'appList',
      'audiencePackages',
      'mediaSlots'
    ]),
    ...mapGetters(['getCampaignById', 'materialsByType']),
    isEdit() {
      return !!this.$route.params.id
    },
    campaignId() {
      return this.$route.params.id
    },
    isArticleCampaign() {
      return this.campaignForm.campaign_type === 'article'
    },
    imageMaterials() {
      return this.materialsByType('image')
    },
    videoMaterials() {
      return this.materialsByType('video')
    },
    docxMaterials() {
      return this.materialsByType('docx')
    },
    selectedImage() {
      return this.materials.find(item => item.id === this.selectedImageId) || null
    },
    selectedVideo() {
      return this.materials.find(item => item.id === this.selectedVideoId) || null
    },
    selectedSoftMaterial() {
      return this.materials.find(item => item.id === this.selectedSoftMaterialId) || null
    },
    selectedAdStyleInfo() {
      return this.adStyles[this.selectedAdStyle] || null
    },
    articlePriceLabel() {
      return '刊例价'
    },
    isThreeImageStyle() {
      return ['3', '34', '63', '66'].includes(this.selectedAdStyle)
    },
    showThreeImageMaterial() {
      return !this.isArticleCampaign && this.isThreeImageStyle
    },
    showImageMaterial() {
      if (this.isArticleCampaign || !this.selectedAdStyleInfo) {
        return false
      }
      return this.selectedAdStyleInfo.material_size !== '视频' && !this.isThreeImageStyle
    },
    showVideoMaterial() {
      if (this.isArticleCampaign || !this.selectedAdStyleInfo) {
        return false
      }
      return this.selectedAdStyleInfo.material_size === '视频'
    },
    selectedImages() {
      return this.selectedImageIds.map(id => this.materials.find(item => item.id === id) || null)
    },
    standardMediaSlots() {
      return this.mediaSlots.filter(slot => slot.adType !== 'soft_article')
    },
    articleMediaSlots() {
      return this.mediaSlots.filter(slot => slot.adType === 'soft_article')
    }
  },
  methods: {
    async loadAdStyles() {
      try {
        this.loadingAdStyles = true
        const response = await fetch('/api/ad-styles')
        const data = await response.json()
        this.adStyles = data.styles || {}
        if (!this.selectedAdStyle && Object.keys(this.adStyles).length) {
          this.selectedAdStyle = Object.keys(this.adStyles)[0]
          this.handleAdStyleChange(this.selectedAdStyle)
        }
      } catch (error) {
        this.$message.error(`加载广告样式失败: ${error.message}`)
      } finally {
        this.loadingAdStyles = false
      }
    },
    async loadDependencies() {
      await Promise.all([
        this.$store.dispatch('fetchCampaigns'),
        this.$store.dispatch('fetchCampaignActivities')
      ])
      await Promise.all([
        this.$store.dispatch('fetchMaterials'),
        this.$store.dispatch('fetchInterestTags'),
        this.$store.dispatch('fetchAppList'),
        this.$store.dispatch('fetchAudiencePackages'),
        this.$store.dispatch('fetchMediaSlots'),
        this.loadAdStyles()
      ])
      this.initForm()
    },
    initForm() {
      if (this.isEdit) {
        const campaign = this.getCampaignById(this.campaignId)
        if (!campaign) {
          return
        }
        this.campaignForm = this.normalizeCampaign(campaign)
      } else {
        const emptyCampaign = createEmptyCampaign()
        emptyCampaign.creative.id = uuidv4()
        emptyCampaign.targeting.id = uuidv4()
        if (this.campaignActivities.length) {
          emptyCampaign.activity_id = this.campaignActivities[0].id
          emptyCampaign.activity_name = this.campaignActivities[0].name
          emptyCampaign.creative.landing_page_url = this.campaignActivities[0].landing_page_url || ''
          emptyCampaign.creative.lead_form_embed_url = this.campaignActivities[0].lead_form_embed_url || ''
          emptyCampaign.creative.lead_form_embed_code = this.campaignActivities[0].lead_form_embed_code || ''
        }
        this.campaignForm = emptyCampaign
      }
      this.syncSelectionStateFromCampaign()
    },
    normalizeCampaign(campaign) {
      const nextCampaign = JSON.parse(JSON.stringify(campaign))
      nextCampaign.campaign_type = nextCampaign.campaign_type || 'standard'
      if (!nextCampaign.activity_id && this.campaignActivities.length) {
        nextCampaign.activity_id = this.campaignActivities[0].id
        nextCampaign.activity_name = this.campaignActivities[0].name
      }
      nextCampaign.creative = {
        id: nextCampaign.creative && nextCampaign.creative.id ? nextCampaign.creative.id : uuidv4(),
        title: '',
        description: '',
        landing_page_url: '',
        lead_form_embed_url: '',
        lead_form_embed_code: '',
        deep_link_url: '',
        win_notice_url: '',
        impression_tracking_url: '',
        click_tracking_url: '',
        deeplink_tracking_url: '',
        materials: [],
        ...(nextCampaign.creative || {})
      }
      const linkedActivity = this.campaignActivities.find(item => item.id === nextCampaign.activity_id)
      if (linkedActivity) {
        nextCampaign.creative.landing_page_url = nextCampaign.creative.landing_page_url || linkedActivity.landing_page_url || ''
        nextCampaign.creative.lead_form_embed_url = nextCampaign.creative.lead_form_embed_url || linkedActivity.lead_form_embed_url || ''
        nextCampaign.creative.lead_form_embed_code = nextCampaign.creative.lead_form_embed_code || linkedActivity.lead_form_embed_code || ''
      }
      nextCampaign.targeting = {
        id: nextCampaign.targeting && nextCampaign.targeting.id ? nextCampaign.targeting.id : uuidv4(),
        gender: [],
        age_range: [],
        device_types: [],
        network_types: [],
        interests: [],
        installed_apps: [],
        brand: [],
        audience_package_ids: [],
        media_slot_ids: [],
        click_filter: false,
        package_names: [],
        ...(nextCampaign.targeting || {})
      }
      nextCampaign.total_budget = Number(nextCampaign.list_price || nextCampaign.total_budget || (nextCampaign.budget && nextCampaign.budget.total_budget) || 0)
      nextCampaign.daily_budget = Number(nextCampaign.daily_budget || (nextCampaign.budget && nextCampaign.budget.daily_budget) || 0)
      nextCampaign.bid_price = Number(nextCampaign.bid_price || 0)
      if (nextCampaign.campaign_type === 'article') {
        nextCampaign.total_budget = this.deriveArticleListPrice(
          nextCampaign.total_budget,
          nextCampaign.budget && nextCampaign.budget.total_spent,
          nextCampaign.id
        )
      }
      if (nextCampaign.campaign_type === 'standard') {
        this.previousStandardBudget = {
          bid_price: nextCampaign.bid_price || DEFAULT_STANDARD_BID_PRICE,
          daily_budget: nextCampaign.daily_budget || DEFAULT_STANDARD_DAILY_BUDGET,
          total_budget: nextCampaign.total_budget || DEFAULT_STANDARD_TOTAL_BUDGET
        }
      }
      return nextCampaign
    },
    getActiveCustomerId() {
      return localStorage.getItem('activeCustomerId') || ''
    },
    getMaterialPreviewUrl(material, asset = 'source') {
      if (!material || !material.id) {
        return ''
      }
      if (asset === 'cover' && !material.cover_url) {
        return ''
      }
      const params = new URLSearchParams()
      const customerId = this.getActiveCustomerId()
      if (customerId) {
        params.set('customer_id', customerId)
      }
      if (asset && asset !== 'source') {
        params.set('asset', asset)
      }
      return `/api/materials/${encodeURIComponent(material.id)}/preview?${params.toString()}`
    },
    roundMoneyToHundreds(value) {
      return Math.round(Number(value || 0) / 100) * 100
    },
    deriveArticleListPrice(currentPrice, totalSpent, seed = '') {
      const current = Number(currentPrice || 0)
      const spent = Number(totalSpent || 0)
      if (!spent) {
        return current >= 10000 ? this.roundMoneyToHundreds(current) : 10000
      }
      const seedScore = String(seed).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      const ratio = 1.08 + ((seedScore % 15) / 100)
      const derived = this.roundMoneyToHundreds(Math.max(spent * ratio, spent + 300))
      const bounded = Math.max(10000, Math.min(500000, derived))
      if (current >= 10000 && Math.abs(current - bounded) <= Math.max(5000, spent * 0.18)) {
        return this.roundMoneyToHundreds(current)
      }
      return bounded
    },
    syncCreativeDefaultsFromActivity(activity, { overwrite = false } = {}) {
      if (!activity) {
        return
      }
      if (this.isArticleCampaign) {
        if (overwrite || !this.campaignForm.creative.lead_form_embed_url) {
          this.campaignForm.creative.lead_form_embed_url = activity.lead_form_embed_url || ''
        }
        if (overwrite || !this.campaignForm.creative.lead_form_embed_code) {
          this.campaignForm.creative.lead_form_embed_code = activity.lead_form_embed_code || ''
        }
        return
      }
      if (overwrite || !this.campaignForm.creative.landing_page_url) {
        this.campaignForm.creative.landing_page_url = activity.landing_page_url || ''
      }
    },
    handleActivityChange(activityId) {
      const activity = this.campaignActivities.find(item => item.id === activityId)
      this.campaignForm.activity_name = activity ? activity.name : ''
      if (!activity) {
        return
      }
      this.syncCreativeDefaultsFromActivity(activity, { overwrite: !this.isEdit })
    },
    syncSelectionStateFromCampaign() {
      const creative = this.campaignForm.creative || {}
      const materials = creative.materials || []
      if (this.isArticleCampaign) {
        const docMaterial = materials.find(item => item.type === 'docx')
        this.selectedSoftMaterialId = docMaterial ? docMaterial.id : ''
        return
      }

      const styleId = creative.ad_style && creative.ad_style.id
        ? String(creative.ad_style.id)
        : (this.campaignForm.ad_style ? String(this.campaignForm.ad_style) : '')
      if (styleId) {
        this.selectedAdStyle = styleId
        if (this.adStyles[styleId]) {
          this.campaignForm.creative.ad_style = {
            id: styleId,
            ...this.adStyles[styleId]
          }
        }
      }
      const imageMaterials = materials.filter(item => item.type === 'image')
      const videoMaterial = materials.find(item => item.type === 'video')
      this.selectedImageId = imageMaterials.length === 1 ? imageMaterials[0].id : ''
      this.selectedVideoId = videoMaterial ? videoMaterial.id : ''
      if (this.isThreeImageStyle) {
        const sortedImages = [...imageMaterials].sort((a, b) => (a.index || 0) - (b.index || 0))
        this.selectedImageIds = [
          sortedImages[0] ? sortedImages[0].id : '',
          sortedImages[1] ? sortedImages[1].id : '',
          sortedImages[2] ? sortedImages[2].id : ''
        ]
      }
    },
    handleCampaignTypeChange(campaignType) {
      if (campaignType === 'article') {
        this.previousStandardBudget = {
          bid_price: this.campaignForm.bid_price || DEFAULT_STANDARD_BID_PRICE,
          daily_budget: this.campaignForm.daily_budget || DEFAULT_STANDARD_DAILY_BUDGET,
          total_budget: this.campaignForm.total_budget || DEFAULT_STANDARD_TOTAL_BUDGET
        }
        this.selectedAdStyle = ''
        this.selectedImageId = ''
        this.selectedVideoId = ''
        this.selectedImageIds = ['', '', '']
        this.campaignForm.bid_price = 0
        this.campaignForm.daily_budget = 0
        this.campaignForm.creative = {
          ...this.campaignForm.creative,
          title: '',
          description: '',
          lead_form_embed_url: '',
          lead_form_embed_code: '',
          deep_link_url: '',
          win_notice_url: '',
          impression_tracking_url: '',
          click_tracking_url: '',
          deeplink_tracking_url: '',
          ad_style: undefined,
          materials: []
        }
        this.campaignForm.targeting = {
          ...this.campaignForm.targeting,
          gender: [],
          age_range: [],
          device_types: [],
          network_types: [],
          interests: [],
          installed_apps: [],
          brand: [],
          audience_package_ids: [],
          media_slot_ids: [],
          click_filter: false,
          package_names: []
        }
        if (this.campaignForm.activity_id) {
          const activity = this.campaignActivities.find(item => item.id === this.campaignForm.activity_id)
          this.syncCreativeDefaultsFromActivity(activity, { overwrite: true })
        }
      } else {
        this.selectedSoftMaterialId = ''
        this.campaignForm.creative.materials = []
        this.campaignForm.creative.lead_form_embed_url = ''
        this.campaignForm.creative.lead_form_embed_code = ''
        if (this.campaignForm.activity_id) {
          const activity = this.campaignActivities.find(item => item.id === this.campaignForm.activity_id)
          this.syncCreativeDefaultsFromActivity(activity, { overwrite: true })
        }
        this.campaignForm.bid_price = this.previousStandardBudget.bid_price || DEFAULT_STANDARD_BID_PRICE
        this.campaignForm.daily_budget = this.previousStandardBudget.daily_budget || DEFAULT_STANDARD_DAILY_BUDGET
        if (!this.campaignForm.total_budget || this.campaignForm.total_budget <= 0) {
          this.campaignForm.total_budget = this.previousStandardBudget.total_budget || DEFAULT_STANDARD_TOTAL_BUDGET
        }
        this.campaignForm.targeting.media_slot_ids = this.campaignForm.targeting.media_slot_ids.filter(id => {
          return this.standardMediaSlots.some(slot => slot.id === id)
        })
        if (!this.selectedAdStyle && Object.keys(this.adStyles).length) {
          this.selectedAdStyle = Object.keys(this.adStyles)[0]
          this.handleAdStyleChange(this.selectedAdStyle)
        }
      }
    },
    handleAdStyleChange(styleId) {
      if (!styleId || this.isArticleCampaign) {
        return
      }
      this.campaignForm.creative.ad_style = {
        id: styleId,
        ...this.adStyles[styleId]
      }
      if (this.isThreeImageStyle) {
        this.selectedVideoId = ''
        this.campaignForm.creative.materials = this.campaignForm.creative.materials.filter(item => item.type !== 'video')
      } else if (this.showVideoMaterial) {
        this.selectedImageId = ''
        this.selectedImageIds = ['', '', '']
        this.campaignForm.creative.materials = this.campaignForm.creative.materials.filter(item => item.type !== 'image')
      } else {
        this.selectedVideoId = ''
        this.campaignForm.creative.materials = this.campaignForm.creative.materials.filter(item => item.type !== 'video')
      }
    },
    handleImageSelect(imageId) {
      const image = this.materials.find(item => item.id === imageId)
      if (!image) {
        return
      }
      const width = this.selectedAdStyleInfo ? this.selectedAdStyleInfo.w : image.width
      const height = this.selectedAdStyleInfo ? this.selectedAdStyleInfo.h : image.height
      this.campaignForm.creative.materials = [{
        id: image.id,
        type: 'image',
        url: image.url,
        width,
        height
      }]
    },
    handleVideoSelect(videoId) {
      const video = this.materials.find(item => item.id === videoId)
      if (!video) {
        return
      }
      const width = this.selectedAdStyleInfo ? this.selectedAdStyleInfo.w : video.width
      const height = this.selectedAdStyleInfo ? this.selectedAdStyleInfo.h : video.height
      this.campaignForm.creative.materials = [{
        id: video.id,
        type: 'video',
        url: video.url,
        width,
        height,
        duration: video.duration || 15
      }]
    },
    handleThreeImageSelect(imageId, index) {
      this.selectedImageIds.splice(index, 1, imageId)
      this.updateThreeImageMaterials()
    },
    updateThreeImageMaterials() {
      const width = this.selectedAdStyleInfo ? this.selectedAdStyleInfo.w : 300
      const height = this.selectedAdStyleInfo ? this.selectedAdStyleInfo.h : 300
      const selectedMaterials = this.selectedImageIds
        .map((id, index) => {
          const image = this.materials.find(item => item.id === id)
          if (!image) {
            return null
          }
          return {
            id: image.id,
            type: 'image',
            url: image.url,
            width,
            height,
            index
          }
        })
        .filter(Boolean)
      this.campaignForm.creative.materials = selectedMaterials
    },
    handleSoftMaterialSelect(materialId) {
      const material = this.materials.find(item => item.id === materialId)
      if (!material) {
        return
      }
      this.campaignForm.creative.materials = [{
        id: material.id,
        type: 'docx',
        url: material.url,
        filename: material.filename,
        title: material.title,
        cover_url: material.cover_url,
        word_count: material.word_count,
        summary: material.summary
      }]
    },
    validateForm() {
      if (!this.campaignForm.name) {
        this.$message.error('请输入广告计划名称')
        return false
      }
      if (!this.campaignForm.activity_id) {
        this.$message.error('请选择所属广告活动')
        return false
      }
      if (this.isArticleCampaign) {
        if (!this.campaignForm.total_budget || this.campaignForm.total_budget < 10000) {
          this.$message.error('请输入软文广告刊例价')
          return false
        }
        if (!this.selectedSoftMaterialId) {
          this.$message.error('请选择DOCX软文素材')
          return false
        }
        if (!this.campaignForm.targeting.media_slot_ids.length) {
          this.$message.error('请选择软广媒体代码位')
          return false
        }
        if (!this.campaignForm.creative.lead_form_embed_code) {
          this.$message.error('请输入软文广告留资代码块')
          return false
        }
        return true
      }
      if (!this.campaignForm.creative.title) {
        this.$message.error('请输入广告标题')
        return false
      }
      if (!this.campaignForm.creative.description) {
        this.$message.error('请输入广告描述')
        return false
      }
      if (!this.campaignForm.creative.landing_page_url) {
        this.$message.error('请输入广告落地页URL')
        return false
      }
      if (!this.campaignForm.creative.materials.length) {
        this.$message.error('请选择广告物料')
        return false
      }
      if (this.showThreeImageMaterial && this.campaignForm.creative.materials.length < 3) {
        this.$message.error('三图样式需要选择三张图片')
        return false
      }
      return true
    },
    buildSubmitData() {
      const submitData = JSON.parse(JSON.stringify(this.campaignForm))
      submitData.campaign_type = submitData.campaign_type || 'standard'
      const activity = this.campaignActivities.find(item => item.id === submitData.activity_id)
      submitData.activity_name = activity ? activity.name : submitData.activity_name
      if (!this.isEdit) {
        submitData.status = 'reviewing'
      }
      if (submitData.campaign_type === 'article') {
        submitData.total_budget = this.deriveArticleListPrice(
          submitData.total_budget,
          submitData.budget && submitData.budget.total_spent,
          submitData.id || submitData.name
        )
        submitData.list_price = submitData.total_budget
        submitData.bid_price = 0
        submitData.daily_budget = 0
        submitData.creative = {
          id: submitData.creative.id || uuidv4(),
          title: submitData.name,
          description: submitData.creative.materials[0] ? submitData.creative.materials[0].summary || '' : '',
          lead_form_embed_url: submitData.creative.lead_form_embed_url || (activity && activity.lead_form_embed_url) || '',
          lead_form_embed_code: submitData.creative.lead_form_embed_code || (activity && activity.lead_form_embed_code) || '',
          materials: submitData.creative.materials.filter(item => item.type === 'docx')
        }
        submitData.targeting = {
          id: submitData.targeting.id || uuidv4(),
          media_slot_ids: submitData.targeting.media_slot_ids
            .filter(id => this.articleMediaSlots.some(slot => slot.id === id))
        }
        return submitData
      }
      submitData.creative.landing_page_url = submitData.creative.landing_page_url || (activity && activity.landing_page_url) || ''
      submitData.targeting.media_slot_ids = submitData.targeting.media_slot_ids
        .filter(id => this.standardMediaSlots.some(slot => slot.id === id))
      delete submitData.list_price
      return submitData
    },
    async submitForm() {
      if (!this.validateForm()) {
        return
      }
      const submitData = this.buildSubmitData()
      if (this.isEdit) {
        const success = await this.$store.dispatch('updateCampaign', {
          id: this.campaignId,
          data: submitData
        })
        if (success) {
          this.$message.success('广告计划更新成功')
          this.$router.push('/campaigns')
        } else {
          this.$message.error('广告计划更新失败')
        }
        return
      }

      const createdCampaign = await this.$store.dispatch('createCampaign', submitData)
      if (createdCampaign) {
        this.$message.success('广告计划创建成功')
        this.$router.push('/campaigns')
      } else {
        this.$message.error('广告计划创建失败')
      }
    },
    previewVideo() {
      this.videoDialogVisible = true
    },
    isBrandSelected(value) {
      return this.campaignForm.targeting.brand.includes(value)
    },
    toggleBrand(value) {
      const index = this.campaignForm.targeting.brand.indexOf(value)
      if (index === -1) {
        this.campaignForm.targeting.brand.push(value)
      } else {
        this.campaignForm.targeting.brand.splice(index, 1)
      }
    },
    showInput() {
      this.inputVisible = true
      this.$nextTick(() => {
        this.$refs.saveTagInput.$refs.input.focus()
      })
    },
    handleInputConfirm() {
      const value = this.inputValue.trim()
      if (value && !this.campaignForm.targeting.package_names.includes(value)) {
        this.campaignForm.targeting.package_names.push(value)
      }
      this.inputVisible = false
      this.inputValue = ''
    },
    removePackageName(index) {
      this.campaignForm.targeting.package_names.splice(index, 1)
    },
    getPlatformName(platform) {
      const item = this.platformOptions.find(option => option.value === platform)
      return item ? item.label : platform
    },
    getAdTypeName(adType) {
      const item = this.adTypeOptions.find(option => option.value === adType)
      return item ? item.label : adType
    },
    getPlatformTagType(platform) {
      const platformMap = {
        tencent: 'success',
        bytedance: 'primary',
        baidu: 'info',
        kuaishou: 'warning',
        alibaba: 'danger',
        xiaomi: 'info',
        oppo: 'success',
        vivo: 'primary',
        huawei: 'warning',
        xiaohongshu_article: 'danger',
        wechat_article: 'success',
        self_media_article: 'warning',
        portal_article: 'info'
      }
      return platformMap[platform] || ''
    },
    getAdTypeTagType(adType) {
      const adTypeMap = {
        banner: '',
        native: 'success',
        interstitial: 'info',
        rewarded_video: 'danger',
        splash: 'warning',
        draw: 'primary',
        soft_article: 'warning'
      }
      return adTypeMap[adType] || ''
    }
  },
  created() {
    this.loadDependencies()
  }
}
</script>

<style scoped>
.campaign-edit {
  padding: 20px 0;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-weight: normal;
}

.page-header p {
  margin: 5px 0 0;
  color: #909399;
}

.form-card {
  margin-bottom: 20px;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  margin-bottom: 50px;
}

.form-actions button {
  margin: 0 10px;
  min-width: 120px;
}

.material-selector {
  display: flex;
  align-items: flex-start;
}

.material-preview {
  margin-left: 20px;
}

.material-preview-doc {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.material-option {
  display: flex;
  align-items: center;
}

.material-option span {
  margin-left: 10px;
}

.doc-option {
  gap: 10px;
}

.doc-option-copy {
  display: flex;
  flex-direction: column;
}

.doc-option-copy small {
  color: #8492a6;
}

.doc-preview-copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.doc-preview-copy span {
  color: #7d8aa3;
  line-height: 1.6;
}

.brand-checkbox-container {
  display: flex;
  flex-wrap: wrap;
  max-height: 150px;
  overflow-y: auto;
}

.brand-checkbox-item {
  width: 100px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.brand-checkbox-item label {
  margin-left: 5px;
}

.form-item-tips {
  color: #909399;
  font-size: 12px;
  margin-top: 5px;
}

.form-item-tips i {
  margin-right: 4px;
}

.review-status-note {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  color: #a16207;
  background: #fef3c7;
  border: 1px solid #fde68a;
  font-size: 13px;
}

.video-filename {
  margin-left: 10px;
  color: #606266;
}

.audience-package-option,
.media-slot-option {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 5px 0;
  white-space: normal;
  word-break: break-all;
}

.audience-package-name,
.media-slot-name {
  font-weight: bold;
  margin-bottom: 4px;
}

.audience-package-info,
.media-slot-info {
  color: #909399;
  font-size: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.three-image-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.three-image-selector {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.three-image-selector .el-select {
  width: 320px;
}

.package-names-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}

.package-name-tag {
  margin: 2px 0;
}

.input-new-tag {
  width: 200px;
}

.button-new-tag {
  border: 1px dashed #d9d9d9;
  color: #999;
  background: #fafafa;
}

.button-new-tag:hover {
  border-color: #409eff;
  color: #409eff;
}

.doc-preview-fallback {
  border-radius: 12px;
  background: linear-gradient(135deg, #2f6bff, #6a8bff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.doc-preview-fallback.small {
  width: 50px;
  height: 30px;
  font-size: 10px;
}

.doc-preview-fallback.large {
  width: 220px;
  height: 132px;
  font-size: 20px;
}
</style>

<style>
.audience-package-dropdown .el-select-dropdown__item,
.media-slot-dropdown .el-select-dropdown__item,
.docx-material-dropdown .el-select-dropdown__item {
  height: auto !important;
  padding: 5px 20px !important;
  line-height: 1.5 !important;
}

.audience-package-dropdown.el-select-dropdown,
.media-slot-dropdown.el-select-dropdown {
  min-width: 400px !important;
}

.docx-material-dropdown.el-select-dropdown {
  width: 640px !important;
  min-width: 640px !important;
  max-width: calc(100vw - 96px) !important;
  overflow: hidden !important;
}

.docx-material-dropdown .doc-option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  width: 100%;
}

.docx-material-dropdown .doc-option-copy {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.docx-material-dropdown .doc-option-copy span {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
